import React, { useEffect, createElement } from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Eye, Clock, Trophy, MessageCircle, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
const benefits = [{
  icon: DollarSign,
  title: 'Fraction of the Cost',
  description: 'Key tags cost a fraction of a custom shirt — making them perfect for giveaways, sponsorships, and bulk handouts without blowing your budget.'
}, {
  icon: Eye,
  title: 'Daily Visibility',
  description: 'Keys go everywhere — car, home, gym, work. Your brand gets seen multiple times a day, every single day, without any extra effort.'
}, {
  icon: Clock,
  title: 'Years of Exposure',
  description: "Unlike a flyer or business card that gets tossed, a key tag stays on a keychain for years. That's thousands of impressions from a single piece."
}, {
  icon: Trophy,
  title: 'Perfect for Any Organization',
  description: 'Businesses, sponsors, racing teams, schools, clubs, and events — key tags work for anyone who wants affordable, long-lasting brand visibility.'
}];
const faqs = [{
  question: 'How much do custom key tags cost?',
  answer: 'Key tags are one of the most affordable branded items we offer. Pricing depends on quantity, size, and design complexity. Text us or request a quote for exact pricing on your project.'
}, {
  question: 'What sizes and shapes are available?',
  answer: 'We offer standard rectangular key tags, rounded shapes, and custom die-cut options. Common sizes range from 1.5" x 3" to 2" x 4". Contact us to discuss what works best for your design.'
}, {
  question: "What's the minimum order?",
  answer: "We keep minimums low to make key tags accessible for any budget. Reach out for current minimum quantities — they're designed to work for small businesses and large orders alike."
}, {
  question: 'How durable are they?',
  answer: "Our key tags are built to last. They're designed to handle daily use on keychains — resistant to scratching, fading, and normal wear and tear for years."
}, {
  question: 'Can I include a QR code or website on the design?',
  answer: "Absolutely. QR codes, URLs, phone numbers, and social handles all work great on key tags. It's a smart way to drive traffic directly from someone's keychain."
}, {
  question: 'How long does production take?',
  answer: 'Most key tag orders are completed within 7–10 business days after artwork approval. Rush options may be available — just let us know your timeline.'
}];
export function CustomKeyTags() {
  useEffect(() => {
    document.title = 'Custom Embroidered Key Tags Michigan — Branded Keychains | Print Atomic';
    const meta = document.querySelector('meta[name="description"]') || document.createElement('meta');
    meta.setAttribute('name', 'description');
    meta.setAttribute('content', 'Custom embroidered key tags in Michigan. Affordable branded keychains for businesses, sponsors, and teams. Daily visibility for years at a fraction of the cost of a shirt.');
    if (!document.querySelector('meta[name="description"]')) {
      document.head.appendChild(meta);
    }
    return () => {
      document.title = 'Print Atomic';
    };
  }, []);
  return <div className="min-h-screen bg-atomic-black" data-id="element-787">
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" data-id="element-788">
        <div className="absolute inset-0 z-0" data-id="element-789">
          <img src="https://cdn.magicpatterns.com/uploads/4H86beJGvkDWT3kXvnAuNV/custom-embroidered-key-tag-michigan.jpg" alt="Custom embroidered Garrett Auto & Truck Service key tags made in Michigan" className="absolute inset-0 w-full h-full object-cover" data-id="element-790" />
          <div className="absolute inset-0 bg-atomic-black/80" data-id="element-791" />
          <div className="absolute inset-0 bg-gradient-to-b from-atomic-black/60 via-atomic-black/70 to-atomic-black/90" data-id="element-792" />
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-793">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl mx-auto text-center" data-id="element-794">
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight" data-id="element-795">
              Custom{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-atomic-green to-lime-400" data-id="element-796">
                Key Tags
              </span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-4" data-id="element-797">
              The most cost-effective way to keep your brand in someone's hands
              — literally. Custom key tags deliver daily visibility for a
              fraction of the cost of a shirt.
            </p>
            <p className="text-base text-gray-400 mb-8 max-w-2xl mx-auto" data-id="element-798">
              Perfect for businesses, sponsors, racing teams, schools, and
              events. One key tag = years of brand impressions.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-799">
              <a href="sms:+15174149232" className="w-full sm:w-auto" data-id="element-800">
                <Button size="lg" className="w-full sm:w-auto gap-2" data-id="element-801">
                  <MessageCircle className="w-5 h-5" data-id="element-802" />
                  Text Us
                </Button>
              </a>
              <a href="https://m.me/atomicMI" target="_blank" rel="noopener noreferrer" title="Facebook Message" aria-label="Facebook Message" className="w-full sm:w-auto" data-id="element-803">
                <Button variant="outline" size="lg" className="w-full sm:w-auto gap-2" data-id="element-804">
                  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true" data-id="element-805">
                    <path d="M12 2C6.36 2 2 6.13 2 11.7c0 2.91 1.2 5.42 3.15 7.2V22l2.95-1.62c.84.23 1.72.36 2.65.36h.25c5.64 0 9.75-4.13 9.75-9.66C20.75 6.13 17.64 2 12 2zm1.08 13.02l-2.5-2.67-4.87 2.67 5.36-5.69 2.56 2.67 4.81-2.67-5.36 5.69z" data-id="element-806" />
                  </svg>
                  Facebook Message
                </Button>
              </a>
              <a href="/contact" className="w-full sm:w-auto" data-id="element-807">
                <Button variant="ghost" size="lg" className="w-full sm:w-auto gap-2" data-id="element-808">
                  Request a Quote
                  <ArrowRight className="w-4 h-4" data-id="element-809" />
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Key Tags */}
      <section className="py-20 bg-atomic-black border-t border-white/5" data-id="element-810">
        <div className="container mx-auto px-4 md:px-6" data-id="element-811">
          <div className="max-w-5xl mx-auto" data-id="element-812">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-813">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-814">
                Why Key Tags Work
              </h2>
              <p className="text-lg text-gray-300" data-id="element-815">
                Low cost, high impact — the smartest branded item you can hand
                out
              </p>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-8" data-id="element-816">
              {benefits.map((benefit, index) => <motion.div key={benefit.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-atomic-charcoal rounded-xl p-6 border border-white/10" data-id="element-817">
                  <div className="w-12 h-12 rounded-lg bg-atomic-green/10 flex items-center justify-center text-atomic-green mb-4 border border-atomic-green/20" data-id="element-818">
                    <benefit.icon size={24} data-id="element-819" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-820">
                    {benefit.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-821">
                    {benefit.description}
                  </p>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* Work Examples */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-822">
        <div className="container mx-auto px-4 md:px-6" data-id="element-823">
          <div className="text-center mb-12" data-id="element-824">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-825">
              Key Tag Examples
            </h2>
            <p className="text-lg text-gray-400" data-id="element-826">
              Durable, vibrant, and ready for daily use
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto" data-id="element-827">
            {[{
            src: 'https://cdn.magicpatterns.com/uploads/smDXQp2Wz82ss3xNsqfwh6/1.jpg',
            alt: 'SP Tools USA custom branded key tags with orange border stitching',
            label: 'Branded Key Tags'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/kAHiweKZn3hDkBuuzetyMd/2.jpg',
            alt: 'Atomic Apparel & Designs custom key tags with red border stitching',
            label: 'Custom Designs'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/wDa5QAKSGq1GGXRRB6QBZB/3.jpg',
            alt: 'Garrett Auto & Truck Service custom key tags with phone number',
            label: 'Business Key Tags'
          }, {
            src: 'https://cdn.magicpatterns.com/uploads/r5sJu8K6S2pJEJnPTkNRmk/4.jpg',
            alt: 'Daily Heating Air Conditioning custom key tags with website and phone',
            label: 'Contractor Key Tags'
          }].map((image, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="group rounded-xl overflow-hidden border border-white/10 hover:border-atomic-green/30 transition-colors" data-id="element-828">
                <div className="aspect-square overflow-hidden" data-id="element-829">
                  <img src={image.src} alt={image.alt} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-830" />
                </div>
                <div className="bg-atomic-black px-4 py-3" data-id="element-831">
                  <span className="text-sm text-gray-300 font-medium" data-id="element-832">
                    {image.label}
                  </span>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Comparison Callout */}
      <section className="py-16 bg-atomic-charcoal border-t border-white/5" data-id="element-833">
        <div className="container mx-auto px-4 md:px-6" data-id="element-834">
          <div className="max-w-4xl mx-auto" data-id="element-835">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-atomic-black rounded-2xl p-8 md:p-10 border border-atomic-green/20 relative overflow-hidden" data-id="element-836">
              <div className="absolute top-0 right-0 w-48 h-48 bg-atomic-green/5 rounded-full blur-3xl -mr-24 -mt-24 pointer-events-none" data-id="element-837" />
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 relative z-10" data-id="element-838">
                Key Tags vs. Custom Shirts
              </h3>
              <div className="grid md:grid-cols-3 gap-6 relative z-10" data-id="element-839">
                <div className="text-center" data-id="element-840">
                  <div className="text-3xl font-bold text-atomic-green mb-2" data-id="element-841">
                    ~90%
                  </div>
                  <p className="text-gray-400 text-sm" data-id="element-842">
                    Lower cost per unit than a custom tee
                  </p>
                </div>
                <div className="text-center" data-id="element-843">
                  <div className="text-3xl font-bold text-atomic-green mb-2" data-id="element-844">
                    365+
                  </div>
                  <p className="text-gray-400 text-sm" data-id="element-845">
                    Days of visibility per year on a keychain
                  </p>
                </div>
                <div className="text-center" data-id="element-846">
                  <div className="text-3xl font-bold text-atomic-green mb-2" data-id="element-847">
                    3–5 yrs
                  </div>
                  <p className="text-gray-400 text-sm" data-id="element-848">
                    Average lifespan on a customer's keys
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-atomic-charcoal border-t border-white/5" data-id="element-849">
        <div className="container mx-auto px-4 md:px-6" data-id="element-850">
          <div className="max-w-3xl mx-auto" data-id="element-851">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-center mb-12" data-id="element-852">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-853">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-gray-300" data-id="element-854">
                Common questions about custom key tags
              </p>
            </motion.div>
            <div className="space-y-6" data-id="element-855">
              {faqs.map((faq, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.05
            }} className="bg-atomic-black rounded-xl p-6 border border-white/10" data-id="element-856">
                  <h3 className="text-lg font-bold text-white mb-3" data-id="element-857">
                    {faq.question}
                  </h3>
                  <p className="text-gray-400 leading-relaxed" data-id="element-858">{faq.answer}</p>
                </motion.div>)}
            </div>
            <motion.div initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} className="mt-8 text-center" data-id="element-859">
              <p className="text-sm text-gray-500 italic" data-id="element-860">
                Embroidery available on select products (outsourced). Contact us
                for details.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
}